import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Loader2, Shield, Key, Users, Mail, Calendar, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminPanel = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  
  // Login form
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  
  // Reset password dialog
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    // Check if already authenticated
    const adminToken = localStorage.getItem('admin_token');
    if (adminToken) {
      setIsAuthenticated(true);
      fetchUsers(adminToken);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('Inserisci username e password');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('admin_token', data.access_token);
        setIsAuthenticated(true);
        toast.success('Accesso effettuato!');
        fetchUsers(data.access_token);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Credenziali non valide');
      }
    } catch (err) {
      console.error('Login error:', err);
      toast.error('Errore di rete');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async (token) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token || localStorage.getItem('admin_token')}` },
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else if (response.status === 401 || response.status === 403) {
        // Token non valido
        localStorage.removeItem('admin_token');
        setIsAuthenticated(false);
        toast.error('Sessione scaduta, effettua nuovamente il login');
      } else {
        toast.error('Errore nel caricamento degli utenti');
      }
    } catch (err) {
      console.error('Fetch users error:', err);
      toast.error('Errore di rete');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }

    setIsResetting(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${selectedUser.id}/reset-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
        body: JSON.stringify({ new_password: newPassword }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowResetDialog(false);
        setSelectedUser(null);
        setNewPassword('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel reset della password');
      }
    } catch (err) {
      console.error('Reset password error:', err);
      toast.error('Errore di rete');
    } finally {
      setIsResetting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setUsers([]);
    toast.info('Logout effettuato');
  };

  const openResetDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowResetDialog(true);
  };

  // Login Form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-primary/5 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-xl sm:text-2xl">Admin Panel</CardTitle>
            <CardDescription className="text-sm">Accesso riservato agli amministratori</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Inserisci username"
                  autoComplete="username"
                  className="text-base"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Inserisci password"
                  autoComplete="current-password"
                  className="text-base"
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Accesso in corso...
                  </>
                ) : (
                  <>
                    <Key className="mr-2 h-4 w-4" />
                    Accedi
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 sm:h-16 items-center justify-between px-3 sm:px-4">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            <h1 className="text-base sm:text-lg font-semibold">Admin Panel</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/')} className="hidden sm:flex">
              Home
            </Button>
            <Button variant="destructive" size="sm" onClick={handleLogout} className="text-xs sm:text-sm px-2 sm:px-4">
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto p-3 sm:p-4 max-w-6xl">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <Users className="w-4 h-4 sm:w-5 sm:h-5" />
              Gestione Utenti
            </CardTitle>
            <CardDescription className="text-sm">
              Lista di tutti gli utenti registrati. Puoi resettare le loro password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <Badge variant="secondary" className="text-xs sm:text-sm">
                    {users.length} {users.length === 1 ? 'utente' : 'utenti'} totali
                  </Badge>
                  <Button size="sm" variant="outline" onClick={() => fetchUsers()} className="w-full sm:w-auto">
                    Ricarica
                  </Button>
                </div>
                <ScrollArea className="h-[500px] sm:h-[600px]">
                  <div className="space-y-3">
                    {users.map((user) => (
                      <Card key={user.id} className="p-3 sm:p-4">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                          <div className="flex-1 min-w-0 w-full">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                {user.avatar_url ? (
                                  <img src={user.avatar_url} alt={user.name} className="w-10 h-10 rounded-full" />
                                ) : (
                                  <span className="text-primary font-semibold">
                                    {user.name.charAt(0).toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold truncate text-sm sm:text-base">{user.name}</p>
                                <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                                  <Mail className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">{user.email}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground pl-0 sm:pl-13">
                              <Calendar className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">
                                Registrato: {new Date(user.created_at).toLocaleDateString('it-IT')}
                              </span>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openResetDialog(user)}
                            className="w-full sm:w-auto text-xs sm:text-sm"
                          >
                            <Lock className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                            Reset Password
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              </>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Reset Password Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="w-[95vw] max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="text-lg">Reset Password</DialogTitle>
            <DialogDescription className="text-sm">
              Imposta una nuova password per {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-sm">Utente</Label>
              <Input value={selectedUser?.email || ''} disabled className="text-sm" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-sm">Nuova Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimo 6 caratteri"
                className="text-base"
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowResetDialog(false)} className="w-full sm:w-auto">
              Annulla
            </Button>
            <Button onClick={handleResetPassword} disabled={isResetting} className="w-full sm:w-auto">
              {isResetting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Reset in corso...
                </>
              ) : (
                'Conferma Reset'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
